import { ArdoqId } from '@ardoq/api-types';
import {
  type PermissionContext,
  permissionsOperations,
} from '@ardoq/access-control';
import { currentUser } from 'models/currentUser';
import { isScenarioMode } from 'models/utils/scenarioUtils';

type HasPermissionFn = (resourceId?: ArdoqId) => boolean;

type HasResourcePermissionFn = (
  permissionContext: PermissionContext,
  resourceId?: ArdoqId
) => boolean;

const hasAdminAccess: HasResourcePermissionFn = (
  permissionContext,
  resourceId
) =>
  currentUser.hasWriteAccess() &&
  (currentUser.isOrgAdmin() ||
    Boolean(
      resourceId &&
      permissionsOperations.hasAdminAccessToResource(
        permissionContext,
        resourceId
      )
    ));

const hasEditAccess: HasResourcePermissionFn = (
  permissionContext,
  resourceId
) =>
  currentUser.hasWriteAccess() &&
  (currentUser.isOrgAdmin() ||
    Boolean(
      resourceId &&
      permissionsOperations.hasEditAccessToResource(
        permissionContext,
        resourceId
      )
    ));

const hasResourceEditAccess: HasResourcePermissionFn = (
  permissionContext,
  resourceId
) =>
  Boolean(
    resourceId &&
    permissionsOperations.hasEditAccessToResource(permissionContext, resourceId)
  );

const hasResourceAdminAccess: HasResourcePermissionFn = (
  permissionContext,
  resourceId
) =>
  Boolean(
    resourceId &&
    permissionsOperations.hasAdminAccessToResource(
      permissionContext,
      resourceId
    )
  );

const hasResourceReadAccess: HasResourcePermissionFn = (
  permissionContext,
  resourceId
) =>
  Boolean(
    resourceId &&
    permissionsOperations.hasReadAccessToResource(permissionContext, resourceId)
  );

const hasReadAccess: HasResourcePermissionFn = (
  permissionContext,
  resourceId
) =>
  currentUser.isOrgAdmin() ||
  hasResourceReadAccess(permissionContext, resourceId);

/**
 * @deprecated Use @ardoq/access-control
 */
export const canCreateTemplateFromModel: HasResourcePermissionFn = (
  permissionContext,
  modelId
) => hasReadAccess(permissionContext, modelId) && currentUser.hasWriteAccess();

const canManageGlobalFields = () => currentUser.isOrgAdmin();

const isOrgWriter = () =>
  currentUser.isOrgAdmin() || currentUser.hasWriteAccess();
const isOrgAdmin = () => currentUser.isOrgAdmin();

const canManagePermissionGroups = () => currentUser.isOrgAdmin();
const canCreateMetamodels = () => currentUser.isOrgAdmin();

const canManageIPAllowlisting = () => currentUser.isOrgAdmin();

// TO-DO, think about:
// Default workspace permissions
// Default survey / dashboard permissions

// Naming scheme: RESOURCE_ACTION
/**
 * @deprecated use @ardoq/access-control
 */
export enum Permissions {
  // Resource-specific permissions
  // Workspace
  WORKSPACE_EDIT,
  WORKSPACE_EDIT_FIELDS,
  WORKSPACE_EDIT_REFERENCE_TYPES,
  WORKSPACE_LOAD_ADDITIONAL,
  WORKSPACE_RELATED_WORKSPACES,
  WORKSPACE_EXPORT_EXCEL,
  // Component
  COMPONENT_VIEW_HISTORY,
  // Rereference
  REFERENCE_VIEW_HISTORY,
  // Survey
  SURVEY_CREATE_FROM_COMPONENT,
  SURVEY_ACCESS,
  SURVEY_EDIT,
  SURVEY_ADMIN,
  // Model
  MODEL_EDIT,
  // Views
  MANAGE_GLOBAL_FIELDS,
  // Bundles
  COPY_BUNDLE,
  // Permission Management
  MANAGE_PERMISSION_GROUPS,
  // Meta Model
  METAMODELS_CREATE,
  // Reports
  REPORTS_CREATE,
  REPORTS_EDIT,
  REPORTS_ADMIN,
  REPORTS_READ,
  // Misc
  IP_ALLOWLIST_MANAGE,
}
const actionToPermission: Partial<Record<Permissions, HasPermissionFn>> = {
  [Permissions.MANAGE_GLOBAL_FIELDS]: canManageGlobalFields,
  [Permissions.COPY_BUNDLE]: isOrgAdmin,
  [Permissions.MANAGE_PERMISSION_GROUPS]: canManagePermissionGroups,
  [Permissions.METAMODELS_CREATE]: canCreateMetamodels,
  [Permissions.REPORTS_CREATE]: isOrgWriter,
  [Permissions.IP_ALLOWLIST_MANAGE]: canManageIPAllowlisting,
};

const actionToResourcePermission: Partial<
  Record<Permissions, HasResourcePermissionFn>
> = {
  // Reports
  [Permissions.REPORTS_EDIT]: hasEditAccess,
  [Permissions.REPORTS_ADMIN]: hasAdminAccess,
  [Permissions.REPORTS_READ]: hasReadAccess,
  // Surveys
  [Permissions.SURVEY_ACCESS]: hasResourceReadAccess,
  [Permissions.SURVEY_EDIT]: hasResourceEditAccess,
  [Permissions.SURVEY_ADMIN]: hasResourceAdminAccess,
};

/**
 * @deprecated Use @ardoq/access-control
 *
 * BE CAREFUL! Some of these "permission checks" mix in ardoq-front state
 * to make a combination of permission and state assertions. Specifically
 * anything that touches currentUser.hasWriteAccess()
 */
export const hasPermission = (
  action: Permissions,
  resourceId?: ArdoqId // we accept undefined in order to support optional property accesses
) => {
  const handler = actionToPermission[action];
  return handler ? handler(resourceId) : false;
};

/**
 * @deprecated Use @ardoq/access-control
 *
 * BE CAREFUL! Some of these "permission checks" mix in ardoq-front state
 * to make a combination of permission and state assertions. Specifically
 * anything that touches currentUser.hasWriteAccess()
 */
export const hasResourcePermission = (
  action: Permissions,
  permissionContext: PermissionContext,
  resourceId?: ArdoqId // we accept undefined in order to support optional property accesses
): boolean => {
  const handler = actionToResourcePermission[action];
  return handler ? handler(permissionContext, resourceId) : false;
};

const actionToDisabled: Partial<Record<Permissions, HasPermissionFn>> = {
  [Permissions.WORKSPACE_EDIT]: isScenarioMode,
  [Permissions.WORKSPACE_EDIT_FIELDS]: isScenarioMode,
  [Permissions.WORKSPACE_EDIT_REFERENCE_TYPES]: isScenarioMode,
  [Permissions.WORKSPACE_LOAD_ADDITIONAL]: isScenarioMode,
  [Permissions.WORKSPACE_RELATED_WORKSPACES]: isScenarioMode,
  [Permissions.WORKSPACE_EXPORT_EXCEL]: isScenarioMode,
  [Permissions.MODEL_EDIT]: isScenarioMode,
  [Permissions.SURVEY_CREATE_FROM_COMPONENT]: isScenarioMode,
  [Permissions.COMPONENT_VIEW_HISTORY]: isScenarioMode,
  [Permissions.REFERENCE_VIEW_HISTORY]: isScenarioMode,
};

/**
 * @deprecated Most of these checks are not permissions checks, but rather state
 * assertions. Move somewhere else.
 */
export const isDisabled = (action: Permissions, resourceId?: ArdoqId) => {
  const handler = actionToDisabled[action];
  return handler ? handler(resourceId || '') : false;
};
